import AddCircle from '@mui/icons-material/AddCircle';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useEffect } from 'react';
import { CHECKBOX, FREE_TEXT, horizontalScrollTableStyle } from '../../constants';
import { BlockProps, ColumnProps, OrganizationRowProps, TabularAnswer } from '../../models/global';
import TextField from '../FormComponents/TextField';
import '../SurveyForm/SurveyForm.scss';

interface TableComponentProps {
  table: {
    columns: [] | ColumnProps[];
    organizationRows?: OrganizationRowProps[];
  };
  language: string;
  sectionId: string;
  addRow: () => void;
  removeRow: (rowId: string) => void;
  changeTabularAnswer: (
    value: string | boolean,
    columnType: string,
    sectionId: string,
    columnId: string,
    rowId: string,
    rowKey: string,
  ) => void;
  tabularAnswers: TabularAnswer[];
  block: BlockProps;
  readonly?: boolean;
  marginTop?: number;
  isAdmin?: boolean;
  isReference?: boolean;
}

function UserTableComponent(props: TableComponentProps) {
  const {
    table,
    language,
    sectionId,
    changeTabularAnswer,
    addRow,
    removeRow,
    tabularAnswers,
    block,
    readonly,
    marginTop,
    isAdmin,
    isReference,
  } = props;

  useEffect(() => {
    const organizationRows = block?.table?.organizationRows;
    if (organizationRows && !organizationRows.length) {
      addRow();
    }
  }, [block]);

  const getField = (type: string, columnId: string, rowId?: string) => {
    switch (type) {
      case CHECKBOX:
        return (
          <Checkbox
            checked={!!tabularAnswers.find(el => el.columnId === columnId && el.organizationRowId === rowId)?.checked}
            onChange={(_, checked) =>
              changeTabularAnswer(checked, CHECKBOX, sectionId, columnId, rowId, 'organizationRowId')
            }
            size='small'
          />
        );
      case FREE_TEXT:
        return (
          <TextField
            multiline
            id='answer'
            label='Answer'
            variant='standard'
            sx={{
              ml: 2,
              ...(isReference && {
                '& .MuiFormLabel-root': {
                  fontSize: '0.9rem',
                },
              }),
            }}
            value={tabularAnswers.find(el => el.columnId === columnId && el.organizationRowId === rowId)?.answer || ''}
            onChange={e =>
              changeTabularAnswer(e.target.value, FREE_TEXT, sectionId, columnId, rowId, 'organizationRowId')
            }
            InputProps={{
              style: {
                fontSize: isReference ? '0.9em' : '1em',
              },
            }}
          />
        );
    }
  };

  return (
    <Box sx={{ mt: marginTop }} className='table-container' style={{ pointerEvents: 'visible' }}>
      <TableContainer component={Paper} sx={{ ...horizontalScrollTableStyle, mt: 2.5 }}>
        <Table sx={{ minWidth: 650 }} aria-label='simple table'>
          <TableHead>
            <TableRow>
              <Box display='flex' pl={!isAdmin ? 5 : 0}>
                {table?.columns.map((tableCol: ColumnProps) => (
                  <TableCell key={tableCol.id}>
                    <Typography variant={isReference ? 'caption' : 'body2'}>
                      {language === 'English' || !tableCol[`name${language}`]
                        ? tableCol.name
                        : `${tableCol[`name${language}`]}`}
                    </Typography>
                  </TableCell>
                ))}
              </Box>
            </TableRow>
          </TableHead>
          <TableBody style={readonly ? { pointerEvents: 'none' } : {}}>
            {!table?.organizationRows?.length && (
              <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                {!isAdmin && (
                  <Box display='flex' pl={2} alignItems='center'>
                    <AddCircle htmlColor='green' onClick={addRow} />
                  </Box>
                )}

                {table?.columns.map((column: ColumnProps, index: number) => (
                  <TableCell component='td' scope='row' key={index}>
                    {getField(column.type, column.id)}
                  </TableCell>
                ))}
              </TableRow>
            )}
            {table?.organizationRows.map((row, rowIndex) => (
              <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                {!isAdmin && (
                  <Box display='flex' pl={2} alignItems='center'>
                    {rowIndex === 0 ? (
                      <AddCircle htmlColor='green' onClick={addRow} fontSize='small' />
                    ) : (
                      /* TODO: re-enable once cross-user row deletion is fixed */
                      <Box width={20} />
                      // <RemoveCircleIcon htmlColor='red' onClick={() => removeRow(row.id)} fontSize='small' />
                    )}
                  </Box>
                )}

                {table?.columns.map((column: ColumnProps, index: number) => (
                  <TableCell component='td' scope='row' key={index}>
                    {getField(column.type, column.id, row.id)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default UserTableComponent;
